import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Eye, 
  Heart, 
  MessageCircle, 
  Plus, 
  Edit, 
  BarChart3,
  TrendingUp,
  Users,
  Calendar
} from 'lucide-react';
import { getAnalytics, getPosts } from '../../firebase/firestore';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [analyticsData, { posts }] = await Promise.all([
          getAnalytics(),
          getPosts(5)
        ]);
        setAnalytics(analyticsData);
        setRecentPosts(posts);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }
  console.log(analytics)

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Fastro Blog</title>
        <meta name="description" content="Admin dashboard for managing Fastro Blog content and analytics" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Admin Dashboard
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage your blog content and monitor performance
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Link
              to="/admin/new"
              className="bg-primary-600 hover:bg-primary-700 text-white p-6 rounded-lg shadow-md transition-colors duration-200"
            >
              <div className="flex items-center">
                <Plus className="h-8 w-8 mr-4" />
                <div>
                  <h3 className="text-lg font-semibold">Create New Post</h3>
                  <p className="text-primary-100">Write and publish a new article</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/posts"
              className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white p-6 rounded-lg shadow-md transition-colors duration-200"
            >
              <div className="flex items-center">
                <Edit className="h-8 w-8 mr-4 text-primary-600 dark:text-primary-400" />
                <div>
                  <h3 className="text-lg font-semibold">Manage Posts</h3>
                  <p className="text-gray-600 dark:text-gray-400">Edit and organize your content</p>
                </div>
              </div>
            </Link>
            <Link
              to="/admin/comments"
              className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white p-6 rounded-lg shadow-md transition-colors duration-200"
            >
              <div className="flex items-center">
                <MessageCircle className="h-8 w-8 mr-4 text-primary-600 dark:text-primary-400" />
                <div>
                  <h3 className="text-lg font-semibold">Manage Comments</h3>
                  <p className="text-gray-600 dark:text-gray-400">Manage and approve comments</p>
                </div>
              </div>
            </Link>
          </div>

          {/* Analytics Cards */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Posts</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {analytics.totalPosts}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <Eye className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Views</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {analytics.totalViews.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                    <Heart className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Likes</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {analytics.totalLikes.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Comments</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {analytics.totalComments}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recent Posts */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recent Posts
                </h2>
                <Link
                  to="/admin/posts"
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentPosts.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No posts yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Get started by creating your first blog post
                  </p>
                  <Link
                    to="/admin/new"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                  >
                    Create Post
                  </Link>
                </div>
              ) : (
                recentPosts.map((post) => (
                  <div key={post.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          <Link
                            to={`/admin/edit/${post.id}`}
                            className="hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {post.excerpt || post.content?.substring(0, 100) + '...'}
                        </p>
                        <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {post.createdAt ? new Date(post.createdAt.toDate()).toLocaleDateString() : 'Recently'}
                          </div>
                          <div className="flex items-center">
                            <Eye className="h-3 w-3 mr-1" />
                            {post.views || 0} views
                          </div>
                          <div className="flex items-center">
                            <Heart className="h-3 w-3 mr-1" />
                            {post.likes || 0} likes
                          </div>
                          <div className="flex items-center">
                            <MessageCircle className="h-3 w-3 mr-1" />
                            {post.comments || 0} comments
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          post.published 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
